export type AnimationPreset =
  // Entrance presets
  | 'none'
  | 'fade'
  | 'fade-up'
  | 'fade-down'
  | 'fade-left'
  | 'fade-right'
  | 'zoom-in'
  | 'zoom-out'
  | 'slide-up'
  | 'slide-down'
  | 'slide-left'
  | 'slide-right'
  | 'blur-in'
  // Text presets
  | 'text-fade'
  | 'text-slide-up'
  | 'text-reveal'
  | 'text-scale'
  // Image presets
  | 'image-fade'
  | 'image-zoom'
  | 'image-slide'
  // Button presets
  | 'button-fade'
  | 'button-lift'
  | 'button-scale'
  | 'button-glow'
  // Card presets
  | 'card-fade'
  | 'card-lift'
  | 'card-scale';

export type AnimationEasing = 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear';
export type AnimationTrigger = 'on-load' | 'on-scroll';
export type HoverEffect = 'none' | 'lift' | 'scale' | 'shadow' | 'glow';

export interface DeviceAnimationOverride {
  enabled?: boolean;
  type?: AnimationPreset;
  duration?: number;
  delay?: number;
}

export interface AnimationConfig {
  enabled: boolean;
  // Entrance / On-load
  entrance?: {
    type: AnimationPreset;
    duration: number; // 100 - 2000 ms
    delay: number; // 0 - 1000 ms
    easing: AnimationEasing;
    trigger: AnimationTrigger;
  };
  // Scroll Animation
  scroll?: {
    enabled: boolean;
    type: AnimationPreset;
    duration: number;
    delay: number;
    threshold: number; // e.g. 0.1, 0.15, 0.2, 0.3
    once?: boolean;
    repeat?: boolean;
  };
  // Hover Animation
  hover?: {
    enabled: boolean;
    type: HoverEffect;
    duration: number; // ms
  };
  // Stagger for children items (cards, services, gallery items)
  staggerChildren?: number; // ms: 0 - 1000
  repeat?: boolean;
  once?: boolean;
  // Responsive switches
  mobileEnabled?: boolean;
  tabletEnabled?: boolean;
  desktopEnabled?: boolean;
  // Responsive overrides
  mobileOverrides?: DeviceAnimationOverride;
  tabletOverrides?: DeviceAnimationOverride;
  desktopOverrides?: DeviceAnimationOverride;
}

export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  enabled: false,
  entrance: {
    type: 'fade-up',
    duration: 700,
    delay: 0,
    easing: 'ease-out',
    trigger: 'on-scroll',
  },
  scroll: {
    enabled: true,
    type: 'fade-up',
    duration: 700,
    delay: 100,
    threshold: 0.15,
    once: true,
    repeat: false,
  },
  hover: {
    enabled: false,
    type: 'lift',
    duration: 250,
  },
  staggerChildren: 100,
  repeat: false,
  once: true,
  mobileEnabled: true,
  tabletEnabled: true,
  desktopEnabled: true,
};

export const ANIMATION_PRESET_OPTIONS: Array<{
  value: AnimationPreset;
  label: string;
  category: 'Genel' | 'Metin' | 'Görsel' | 'Buton' | 'Kart';
}> = [
  { value: 'none', label: 'Yok', category: 'Genel' },
  { value: 'fade', label: 'Fade (Belirme)', category: 'Genel' },
  { value: 'fade-up', label: 'Fade Up (Aşağıdan Yukarı)', category: 'Genel' },
  { value: 'fade-down', label: 'Fade Down (Yukarıdan Aşağı)', category: 'Genel' },
  { value: 'fade-left', label: 'Fade Left (Soldan Sağa)', category: 'Genel' },
  { value: 'fade-right', label: 'Fade Right (Sağdan Sola)', category: 'Genel' },
  { value: 'zoom-in', label: 'Zoom In (Yakınlaşarak)', category: 'Genel' },
  { value: 'zoom-out', label: 'Zoom Out (Uzaklaşarak)', category: 'Genel' },
  { value: 'slide-up', label: 'Slide Up (Kayarak Yukarı)', category: 'Genel' },
  { value: 'slide-down', label: 'Slide Down (Kayarak Aşağı)', category: 'Genel' },
  { value: 'slide-left', label: 'Slide Left (Kayarak Sola)', category: 'Genel' },
  { value: 'slide-right', label: 'Slide Right (Kayarak Sağa)', category: 'Genel' },
  { value: 'blur-in', label: 'Blur In (Netleşerek)', category: 'Genel' },

  { value: 'text-fade', label: 'Metin Belirme', category: 'Metin' },
  { value: 'text-slide-up', label: 'Metin Yukarı Kayma', category: 'Metin' },
  { value: 'text-reveal', label: 'Metin Reveal (Harf/Satır)', category: 'Metin' },
  { value: 'text-scale', label: 'Metin Büyüyerek Açılış', category: 'Metin' },

  { value: 'image-fade', label: 'Görsel Belirme', category: 'Görsel' },
  { value: 'image-zoom', label: 'Görsel Zoom & Focus', category: 'Görsel' },
  { value: 'image-slide', label: 'Görsel Paralaks Kayma', category: 'Görsel' },

  { value: 'button-fade', label: 'Buton Yumuşak Giriş', category: 'Buton' },
  { value: 'button-lift', label: 'Buton Hover Lift', category: 'Buton' },
  { value: 'button-scale', label: 'Buton Scale (Büyüme)', category: 'Buton' },
  { value: 'button-glow', label: 'Buton Glow (Işıma)', category: 'Buton' },

  { value: 'card-fade', label: 'Kart Belirme', category: 'Kart' },
  { value: 'card-lift', label: 'Kart Hover Havalanma', category: 'Kart' },
  { value: 'card-scale', label: 'Kart Odaklanma', category: 'Kart' },
];

export const DURATION_PRESETS = [100, 200, 300, 400, 500, 700, 1000, 1500, 2000];
export const DELAY_PRESETS = [0, 50, 100, 150, 200, 300, 500, 1000];
export const EASING_OPTIONS: Array<{ value: AnimationEasing; label: string }> = [
  { value: 'ease-out', label: 'Ease Out (Önerilen - Akıcı)' },
  { value: 'ease', label: 'Ease (Standart)' },
  { value: 'ease-in', label: 'Ease In (Hızlanan)' },
  { value: 'ease-in-out', label: 'Ease In-Out (Dengeli)' },
  { value: 'linear', label: 'Linear (Sabit Hız)' },
];
